import React, { useRef, useEffect, useState, useCallback } from 'react'
import Gap from './Gap'
import MentionSuggestionList from './MentionSuggestionList'
import { useMentionSuggestions } from '../hooks/useMentionSuggestions'
import { applyMention } from '../utils/mention'
import { registerComposing } from '../utils/composingState'

interface CommentInputSectionProps {
  commentText: string
  replyingToComment: {
    authorLabel: string
  } | null
  editingComment?: {
    commentId: string
  } | null
  isSubmittingComment: boolean
  onCommentTextChange: (text: string) => void
  onSubmit: () => void
  /**
   * 값이 바뀔 때마다 입력칸에 커서를 놓는다.
   *
   * 댓글 버튼은 자세히보기·사진 화면·피드 세 곳에 있는데, 눌러도 아무 일이
   * 없었다. 여기로 신호를 보내면 입력칸이 보이는 자리로 올라오고 커서가 잡힌다.
   * boolean이 아니라 숫자인 이유는, 같은 화면에서 두 번 눌렀을 때도 값이
   * 달라져야 다시 반응하기 때문이다.
   */
  focusToken?: number
  onCancelReply?: () => void
  onCancelEdit?: () => void
}

const CommentInputSection: React.FC<CommentInputSectionProps> = ({
  commentText,
  replyingToComment,
  editingComment = null,
  isSubmittingComment,
  focusToken,
  onCommentTextChange,
  onSubmit,
  onCancelReply,
  onCancelEdit,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  /*
   * 화면을 떠날 때 키보드를 내린다.
   *
   * 키보드를 올린 채로 다른 기록으로 넘어가면, 새 화면이 이미 줄어든 창에서
   * 그려지는데 커서는 사라진 상태가 된다. 웹킷이 그걸 다시 맞추면서 화면이
   * 한 번 크게 밀렸다가 돌아온다. (실기기 영상 8.00초 → 8.27초)
   *
   * 떠나기 전에 커서를 거두면 키보드가 먼저 내려가므로, 다음 화면은 평소
   * 크기에서 시작한다. 맞출 것이 없으니 밀리지도 않는다.
   */
  useEffect(() => {
    return () => {
      const el = document.activeElement
      if (el instanceof HTMLElement) {
        el.blur()
      }
    }
  }, [])

  /**
   * 커서 위치. "@"를 어디까지 쳤는지 알아야 추천 목록을 띄울 수 있다.
   * 포커스가 없으면 null로 두어 목록이 뜨지 않게 한다.
   */
  const [caret, setCaret] = useState<number | null>(null)

  /** 삽입 후 커서를 옮겨야 할 위치. 다시 그려진 뒤에 적용한다. */
  const pendingCaretRef = useRef<number | null>(null)

  const { suggestions } = useMentionSuggestions(commentText, caret)

  /** 입력칸에 포커스가 있는지 — 쓰는 중 판단에 쓴다 */
  const [isFocused, setIsFocused] = useState(false)

  /*
   * 커서를 잡되 화면도 입력칸으로 보낸다.
   *
   * iOS 웹뷰는 사용자의 손짓 없이 focus()만 부르면 키보드를 안 올리는 경우가
   * 있다. 같은 화면에서 누른 경우(손짓 안에서 실행)에는 올라오고, 다른 화면에서
   * 넘어온 경우에는 안 올라올 수 있다. 그때도 입력칸이 눈앞에 와 있으면
   * 한 번 더 탭해서 바로 쓸 수 있다.
   */
  useEffect(() => {
    // 0은 '아직 아무도 안 눌렀다'는 뜻이다. 그냥 화면을 연 것만으로
    // 키보드가 올라오면 안 되므로 여기서 멈춘다.
    if (!focusToken) return

    const el = textareaRef.current
    if (!el) return

    /*
     * preventScroll이 핵심이다.
     *
     * 그냥 focus()를 부르면 브라우저가 '이 칸을 보이게 하라'는 뜻으로 받아들여
     * 페이지를 통째로 굴린다. 그런데 입력칸은 하단에 고정돼 이미 눈앞에 있다.
     * 굴릴 이유가 없는데 굴리니, 화면이 아래로 밀렸다가 키보드가 자리를 잡으면서
     * 되돌아온다. 아이콘을 눌렀을 때 화면이 한 번 출렁이던 게 이것이다.
     *
     * 보이게 하는 일은 부르는 쪽(자세히보기)이 본문을 내려서 이미 하고 있다.
     * 여기서는 커서만 놓는다.
     */
    el.focus({ preventScroll: true })
  }, [focusToken])

  /**
   * 쓰는 동안에는 스와이프 뒤로가기를 재운다.
   *
   * 포커스만 보면 부족하다. 다른 댓글의 글자를 길게 눌러 복사하려는 순간
   * 입력칸의 포커스가 풀리기 때문이다. 그래서 '쓰던 내용이 남아 있는 동안'도
   * 함께 본다. 둘 중 하나라도 해당하면 아직 쓰는 중이다.
   */
  useEffect(() => {
    if (!isFocused && commentText.trim() === '') {
      return
    }
    const release = registerComposing()

    return release
  }, [isFocused, commentText])

  /*
   * 답글·수정으로 들어올 때도 커서만 놓는다.
   *
   * 여기만 맨 focus()였다. 그래서 댓글칸을 직접 누를 때(preventScroll)는
   * 멀쩡한데 '답글'을 누르면 화면이 한 번 밀렸다 돌아왔다 — 위 focusToken
   * 주석에 적어둔 바로 그 증상이다. 입력칸은 하단에 고정돼 이미 눈앞에
   * 있으므로 브라우저가 굴릴 이유가 없다. 세 경로 모두 같은 호출을 쓴다.
   */
  useEffect(() => {
    if ((replyingToComment || editingComment) && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true })
    }
  }, [replyingToComment, editingComment])

  /** 입력·클릭·키 이동 등 커서가 움직일 만한 모든 경우를 한 곳에서 반영 */
  const syncCaret = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    setCaret(textarea.selectionStart)
  }, [])

  // 아이디를 넣은 직후 커서를 그 뒤로 옮긴다.
  // (값만 바꾸면 브라우저가 커서를 맨 끝으로 보내버려 이어서 쓰기가 어긋난다)
  useEffect(() => {
    const next = pendingCaretRef.current
    if (next === null) {
      return
    }
    pendingCaretRef.current = null

    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    textarea.focus({ preventScroll: true })
    textarea.setSelectionRange(next, next)
    setCaret(next)
  }, [commentText])

  const handleSelectMention = useCallback(
    (username: string) => {
      const textarea = textareaRef.current
      const at = textarea ? textarea.selectionStart : caret
      if (at === null) {
        return
      }

      const result = applyMention(commentText, at, username)
      pendingCaretRef.current = result.caret
      onCommentTextChange(result.text)
    },
    [commentText, caret, onCommentTextChange]
  )

  // 텍스트 양에 따라 textarea 높이 자동 조절 (최대 4줄)
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight)
    const maxHeight = lineHeight * 4
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${Math.max(lineHeight, newHeight)}px`
  }, [commentText])

  const handleSubmit = () => {
    if (!commentText.trim() || isSubmittingComment) {
      return
    }
    setCaret(null) // 제출 후 추천 목록이 남아 있지 않게
    onSubmit()
  }

  return (
    <div className="w-full app-container">
      {/* 답글 작성 중 또는 댓글 수정 중일 때 표시 */}
      {(replyingToComment || editingComment) && (
        <>
          <Gap height={6} />
          <div className="w-full bg-ivoryKey px-20px flex items-center justify-between">
            <span className="font-pretendard font-regular text-14px text-textColorText2">
              {replyingToComment ? (
                <>
                  <strong className="font-bold">
                    {replyingToComment.authorLabel}
                  </strong>
                  님에게 답글
                </>
              ) : (
                <>댓글 수정</>
              )}
            </span>

            {replyingToComment && onCancelReply && (
              <button
                type="button"
                onClick={onCancelReply}
                className="font-pretendard font-regular text-14px text-textColorText3"
              >
                취소
              </button>
            )}
            {editingComment && onCancelEdit && (
              <button
                type="button"
                onClick={onCancelEdit}
                className="font-pretendard font-regular text-14px text-textColorText3"
              >
                취소
              </button>
            )}
          </div>
        </>
      )}

      <Gap height={6} />

      {/*
        언급(@) 추천 — 입력창 바로 위에 붙여 키보드에 가리지 않게 한다.
        입력바와 같은 배경색이라, 사이에 간격을 두면 그 틈으로 페이지 배경이
        비쳐 이음매처럼 보인다. 그래서 목록과 입력바는 딱 붙여 둔다.
      */}
      {suggestions.length > 0 && (
        <MentionSuggestionList
          suggestions={suggestions}
          onSelect={handleSelectMention}
        />
      )}

      <div className="w-full bg-ivoryKey px-16px">
        {/* 댓글 입력 영역 - 입력 박스 안에 우측 전송 아이콘 */}
        <div
          className={`
            relative
            w-full
            flex
            items-center
            min-h-44px
            py-7px
            px-12px
            bg-white
            rounded-50px
            border
            border-solid
            border-472410-10%
          `}
        >
          <textarea
            ref={textareaRef}
            value={commentText}
            onChange={(e) => {
              onCommentTextChange(e.target.value)
              setCaret(e.target.selectionStart)
            }}
            onFocus={() => {
              setIsFocused(true)
              syncCaret()
            }}
            onClick={syncCaret}
            onKeyUp={syncCaret}
            onSelect={syncCaret}
            onBlur={() => {
              setIsFocused(false)
              // 커서를 지우면 추천 목록도 닫힌다.
              // 추천 항목을 누른 경우엔 그쪽에서 onMouseDown/onTouchStart를 막아
              // 포커스가 유지되므로, 여기까지 오는 건 정말로 입력창을 벗어난 경우다.
              // (받아둔 맞팔 목록은 그대로 둔다 — 다시 "@"를 칠 때 재요청하지 않게)
              setCaret(null)
            }}
            placeholder="댓글을 입력하세요"
            maxLength={500}
            disabled={isSubmittingComment}
            rows={1}
            className={`
              flex-1
              min-h-20px
              max-h-120px
              py-0
              pr-8px
              bg-transparent
              border-none
              font-pretendard
              font-regular
              text-16px
              leading-180%
              text-brown-80%-main
              placeholder:text-brown-80%-main
              focus:outline-none
              resize-none
              overflow-y-auto
              scrollbar-hide
            `}
          />
          {/*
            커서가 없을 때만 덮는 투명한 층.

            왜 필요한가 — 실기기로 잰 값
            ---------------------------
            칸을 직접 누르면 웹킷이 '이 칸을 보이게' 하려고 보이는 영역을
            380px 옮긴다. 앱도 웹뷰를 줄이고 있어 화면이 크게 밀렸다 돌아온다.
              0ms off0 top62 → 1658ms off380 top-698 → 2331ms off0 top62

            댓글 아이콘으로 누를 때는 이 현상이 없다. 아이콘은 div라서
            브라우저의 기본 포커스 동작이 없고, 우리가 preventScroll로 커서만
            놓기 때문이다. 그래서 입력칸 위에도 같은 성격의 층을 덮어,
            누르는 대상이 입력칸이 아니라 div가 되게 한다.

            기본 동작을 막는 게 아니라 대상이 바뀌는 것이라, 키보드를 띄우는
            손짓 연결은 그대로 살아 있다. (preventDefault로 막았을 때는
            키보드가 아예 안 올라왔다.)

            언제 치우나 — 커서 유무가 아니라 '쓸 글자가 있을 때'다.

            처음에는 커서가 잡히면 치웠는데, 그러면 댓글 아이콘으로 들어온
            경우에 구멍이 생긴다. 그때는 화면이 열리자마자 커서가 잡히므로
            층이 없고, 사용자가 입력칸을 누르면 맨 입력칸을 직접 누르는 게
            되어 화면이 밀린다. (실기기 15회 중 밀린 11회가 이 경로였다)
            그래서 커서가 있어도 **빈 칸이면** 층을 유지한다.

            층을 치우는 건 글자 사이를 눌러 커서를 옮겨야 할 때뿐이다.
            빈 칸에서는 옮길 커서가 없으니 잃는 게 없다.

            오른쪽 끝은 비워 둔다. 전송 버튼까지 덮으면, 글을 써 둔 채 키보드를
            내렸다가 보내려 할 때 버튼이 안 눌린다.
          */}
          {!(isFocused && commentText.length > 0) && (
            <div
              className="absolute left-0 top-0 bottom-0 right-40px z-[1]"
              onClick={() => textareaRef.current?.focus({ preventScroll: true })}
            />
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!commentText.trim() || isSubmittingComment}
            className={`
              flex-shrink-0
              w-24px
              h-24px
              flex
              items-center
              justify-center
              bg-transparent
              border-none
              cursor-pointer
            `}
          >
            {/* /images/*는 max-age 1년 + immutable로 캐시된다. 파일명이 같으면
                내용을 바꿔도 기기가 옛 이미지를 계속 쓰므로 버전을 붙인다. */}
            <img
              src="/images/chat-send-icon.png?v=2"
              alt="전송"
              className="w-full h-full object-contain"
            />
          </button>
        </div>
      </div>

      <Gap height={6} />
    </div>
  )
}

export default CommentInputSection
